<script lang="ts">
import { defineBlock } from '@bagelink/blox'
import { defineSchema, $ } from '@bagelink/vue'
const defaults = {
	name: 'Author Name',
	title: 'Prof.',
	image: '/webclip.png',
	bio: 'Short author biography.',
	link: '/author/author-name',
	boldName: false,
	rows: 3,
	mobileRows: 3,
}
const schema = defineSchema({
	name: $.text('Name'),
	title: $.text('Title'),
	image: $.upload('Image'),
	bio: $.richtext('Bio', { basic: true }),
	link: $.text('Link'),
	boldName: $.toggle('Bold Name'),
	rows: $.text('Ellipsis Rows'),
	mobileRows: $.text('Mobile Ellipsis Rows'),
})
export const AuthorCard = defineBlock({
	pickerHidden: true,
	thumbnail: '/block-thumbs/AuthorCard.svg',
	category: 'Content',
	order: 50,
	name: 'AuthorCard',
	label: 'Author Card',
	icon: 'person',
	schema,
	defaults,
})
</script>
<script setup lang="ts">

import { Image } from '@bagelink/vue'
import { computed } from 'vue'

interface AuthorObj {
	name?: string
	title?: string
	image?: string
	bio?: string
	link?: string
}




// `author` is an object convenience prop used by list callers (BlockAllAuthors).
// Flat props are what the CMS editor edits; flat wins, object fills the gaps.
const props = withDefaults(
	defineProps<{
		name?: string
		title?: string
		image?: string
		bio?: string
		link?: string
		boldName?: boolean
		showName?: boolean
		articleLayout?: boolean
		articleExcerptLength?: number
		rows?: number
		mobileRows?: number
		author?: AuthorObj
	}>(),
	{
		boldName: false,
		showName: true,
		articleLayout: false,
		articleExcerptLength: 240,
		rows: 3,
		mobileRows: 3,
	},
)

const name = computed(() => props.name ?? props.author?.name ?? '')
const image = computed(() => props.image ?? props.author?.image ?? '/webclip.png')
const bio = computed(() => props.bio ?? props.author?.bio ?? '')
const inlineBio = computed(() =>
	bio.value.replace(/<\/?p(?:\s[^>]*)?>/gi, '').replace(/<br\s*\/?>/gi, ' '),
)
const plainBio = computed(() =>
	inlineBio.value
		.replace(/<[^>]+>/g, '')
		.replace(/&nbsp;/gi, ' ')
		.replace(/&amp;/gi, '&')
		.replace(/&quot;/gi, '"')
		.replace(/&#39;|&apos;/gi, "'")
		.replace(/\s+/g, ' ')
		.trim(),
)
const articleExcerpt = computed(() => {
	if (plainBio.value.length <= props.articleExcerptLength) return plainBio.value

	const excerpt = plainBio.value.slice(0, props.articleExcerptLength + 1)
	return excerpt.slice(0, excerpt.lastIndexOf(' ')).trimEnd()
})
const articleExcerptIsTruncated = computed(
	() => articleExcerpt.value.length < plainBio.value.length,
)
const articleExcerptBoldLead = computed(() => {
	const match = inlineBio.value.match(/^\s*<(?:strong|b)(?:\s[^>]*)?>(.*?)<\/(?:strong|b)>/i)
	if (!match) return ''

	return match[1].replace(/<[^>]+>/g, '').trim()
})
const articleExcerptRemainder = computed(() => {
	if (
		!articleExcerptBoldLead.value ||
		!articleExcerpt.value.startsWith(articleExcerptBoldLead.value)
	) {
		return articleExcerpt.value
	}

	return articleExcerpt.value.slice(articleExcerptBoldLead.value.length)
})
const link = computed(() => props.link ?? props.author?.link ?? '/')
const bioLeadsWithName = computed(() => {
	const label = name.value.toLowerCase().replace(/[^a-z0-9]+/g, '')
	if (label.length < 3) return false
	const lead = plainBio.value.slice(0, 180).toLowerCase().replace(/[^a-z0-9]+/g, '')
	const at = lead.indexOf(label)
	return at >= 0 && at <= 12
})

const ellipsisClass = `ellipsis-${props.rows}${
	props.mobileRows ? ` m_ellipsis-${props.mobileRows}` : ''
}`
</script>

<template>
	<router-link
		:to="link || '/'"
		class="decoration-none author-card flex gap-1 align-items-start bg-gray mb-1 p-1"
	>
		<Image
			:src="image"
			:alt="name"
			ratio="1"
			class="h60px aspect-ratio-1 cover round flex-shrink-0"
		/>
		<div class="txt-start txt15">
			<div :class="[articleLayout ? 'relative' : ellipsisClass]">
				<p v-if="showName && name && !bioLeadsWithName" class="inline-block pe-025" :class="{ bold: boldName }">
					{{ name }}
				</p>
				<span v-if="bio && articleLayout" class="inline light">
					<strong v-if="articleExcerptBoldLead">{{ articleExcerptBoldLead }}</strong
					>{{ articleExcerptRemainder }}
				</span>
				<span v-else-if="bio" class="inline light" v-html="inlineBio" />
				<span v-if="bio && articleLayout" class="inline bg-gray txt15 hidePrint">
					<span v-if="articleExcerptIsTruncated" class="color-black">… </span>
					<span class="color-primary italic">Read More</span>
				</span>
			</div>
			<div v-if="bio && !articleLayout" class="color-primary txt15 italic hidePrint">Read More</div>
		</div>
	</router-link>
</template>
